import { formatTimeSpacedAmPm } from '../../../../utils/formatting/timeFormatting';
import { WAIVER_FORBIDDEN_REASONS, BREAK_STEPS } from '../../../../utils/constants/breakConstants';

/**
 * Get the human-readable name for a break type
 * @param {string} type - The break type (e.g., 'MEAL_BREAK', 'REST_BREAK')
 * @returns {string} The formatted break type name
 */
export const getBreakTypeName = (type) => {
  return type === 'MEAL_BREAK' ? 'Meal Break' : 'Rest Break';
};

/**
 * Format a timestamp to a readable time string
 * @param {Object|string} timestamp - The timestamp to format
 * @returns {string} The formatted time string
 */
export const formatBreakTime = (timestamp) => {
  if (!timestamp) return 'Not scheduled';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return formatTimeSpacedAmPm(date);
};

/**
 * Get a human-readable message for why a break cannot be waived
 * @param {Object} breakItem - The break item with waiver information
 * @returns {string} The formatted reason message
 */
export const getBreakForbiddenReasonString = (breakItem) => {
  const forbiddenReason = breakItem.waivingForbiddenReason;

  if (!Object.values(WAIVER_FORBIDDEN_REASONS).includes(forbiddenReason)) {
    console.warn(`Invalid waiver forbidden reason: ${forbiddenReason}`);
    return 'This break cannot be waived due to policy restrictions.';
  }

  if (forbiddenReason === WAIVER_FORBIDDEN_REASONS.SHIFT_TOO_LONG) {
    return 'State law requires a meal break for shifts of this length.';
  }
  if (forbiddenReason === WAIVER_FORBIDDEN_REASONS.FIRST_BREAK_WAS_WAIVED) {
    return 'The first break was waived, so this break cannot be waived.';
  }
  if (forbiddenReason === WAIVER_FORBIDDEN_REASONS.SECOND_BREAK_WAS_WAIVED) {
    return 'The second break was waived, so this break cannot be waived.';
  }
  if (forbiddenReason === WAIVER_FORBIDDEN_REASONS.CANNOT_WAIVE_REST_BREAK) {
    return 'State law requires that rest breaks must be taken.';
  }

  return 'This break cannot be waived due to policy restrictions.';
};

/**
 * Get the appropriate back button action based on current step
 * @param {string} currentStep - The current step in the modal
 * @param {Function} onClose - Function to close the modal
 * @param {Function} handleBackToOptions - Function to go back to options
 * @param {Function} handleBackToSelection - Function to go back to selection
 * @returns {Function} The appropriate back button handler
 */
export const getBackButtonAction = (currentStep, onClose, handleBackToOptions, handleBackToSelection) => {
  switch (currentStep) {
    case BREAK_STEPS.OPTIONS:
      return onClose; // Go back to main interface
    case BREAK_STEPS.WAIVER_BREAK_SELECTION:
      return handleBackToOptions; // Go back to options
    case BREAK_STEPS.WAIVER_CONFIRMATION:
      return handleBackToSelection; // Go back to selection
    case BREAK_STEPS.START_BREAK_CONFIRMATION:
      return handleBackToOptions; // Go back to options
    default:
      return onClose;
  }
};
